import React, { useCallback } from 'react';
import { twMerge } from 'tailwind-merge';

import {
    closeMeetingHostControls,
    muteAllParticipants,
    turnOffAllParticipantsVideo,
} from 'flow/actions/meeting-actions';
import { updateMeetingAccess } from 'flow/actions/meeting-lobby-actions';
import Button from 'flow/components/ui-components/Button';
import Icon from 'flow/components/ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import {
    selectActiveMeetingChatSessionKey,
    selectActiveMeetingMeetingAccess,
    selectActiveMeetingScreenShareMemberName,
    selectShouldShowHostControlsPanel,
} from 'flow/selectors/meeting-selectors';
import { MeetingAccess } from 'flow/utils/meeting-constants';
import MeetingAccessDropdown from '../modals/VideoMeetingRoom/MeetingAccessDropdown';
import { getRoomHostControlsVisibility } from './MeetingParticipantsList/roomHostControls';
import useMeetingMembers from './MeetingParticipantsList/useMeetingMembers';

/**
 * Host controls panel content (VID-53), framed by the shared MeetingRightPanel
 * slot: meeting access above audio/video controls. Its entry points only open
 * it for the room admin, but the open/closed flag can outlive the meeting it
 * was opened in (rejoining a different meeting as a plain member), so the
 * panel re-checks selectShouldShowHostControlsPanel itself rather than
 * trusting it was only ever opened correctly.
 */
const MeetingHostControlsPanel = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();

    const shouldShow = useAppSelector(selectShouldShowHostControlsPanel);

    const { members } = useMeetingMembers();
    const screenShareMemberName = useAppSelector(
        selectActiveMeetingScreenShareMemberName,
    );
    const { anyOtherAudioActive, anyOtherVideoActive } =
        getRoomHostControlsVisibility(members, screenShareMemberName);
    // Not yet explicitly set (an older room) is not "still loading" here —
    // the meeting is already active, so its data has already loaded.
    const activeMeetingAccess = useAppSelector(
        selectActiveMeetingMeetingAccess,
    );
    const meetingAccess =
        activeMeetingAccess ?? MeetingAccess.ANYONE_WITH_LINK.key;
    const chatSessionKey = useAppSelector(selectActiveMeetingChatSessionKey);

    const muteAll = useCallback(
        () => dispatch(muteAllParticipants()),
        [dispatch],
    );
    const turnOffAllCameras = useCallback(
        () => dispatch(turnOffAllParticipantsVideo()),
        [dispatch],
    );
    const changeMeetingAccess = useCallback(
        (newMeetingAccess: string) =>
            dispatch(updateMeetingAccess(chatSessionKey, newMeetingAccess)),
        [dispatch, chatSessionKey],
    );

    if (!shouldShow) {
        return null;
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-gray-800 text-white">
            <div className="flex items-center justify-between px-4 py-3 text-base font-bold">
                <span>{i18next.t('videoconference.host_controls.title')}</span>
                <button
                    className="flex cursor-pointer items-center border-none bg-transparent p-0"
                    onClick={() => dispatch(closeMeetingHostControls())}
                    aria-label={i18next.t('videoconference.controlpanel.close')}
                    type="button"
                >
                    <Icon iconKey="close" />
                </button>
            </div>

            <div className="flex flex-1 flex-col gap-4 overflow-auto px-4 pb-4 text-sm">
                <MeetingAccessDropdown
                    value={meetingAccess}
                    onChange={changeMeetingAccess}
                    videoDark={true}
                />

                <hr className="my-1 border-t border-gray-700" />

                <div className="flex flex-col gap-3">
                    <div className="font-bold">
                        {i18next.t(
                            'videoconference.host_controls.audio_and_video',
                        )}
                    </div>

                    <div className="flex flex-col gap-2">
                        <Button
                            color="basic"
                            className={twMerge(
                                '!h-11 w-full !min-w-0 justify-center gap-2 !rounded-xl border border-solid border-gray-600 !bg-transparent !text-white',
                                !anyOtherAudioActive &&
                                    'disabled:!bg-transparent disabled:!text-gray-500',
                            )}
                            onClick={muteAll}
                            disabled={!anyOtherAudioActive}
                        >
                            <Icon iconKey="mic_outlined" size="1rem" />
                            {i18next.t(
                                'videoconference.host_controls.mute_all',
                            )}
                        </Button>

                        <Button
                            color="basic"
                            className={twMerge(
                                '!h-11 w-full !min-w-0 justify-center gap-2 !rounded-xl border border-solid border-gray-600 !bg-transparent !text-white',
                                !anyOtherVideoActive &&
                                    'disabled:!bg-transparent disabled:!text-gray-500',
                            )}
                            onClick={turnOffAllCameras}
                            disabled={!anyOtherVideoActive}
                        >
                            <Icon iconKey="video" size="1rem" />
                            {i18next.t(
                                'videoconference.host_controls.turn_off_all_cameras',
                            )}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MeetingHostControlsPanel;
